import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search,
  Plus,
  Package,
  AlertTriangle,
  PackageX,
  ArrowUpDown,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import Layout from "../components/Layout";
import ProductCard from "../components/ProductCard";
import { useAuth } from "../context/AuthContext";
import { formatNPR, formatQty, formatSize } from "../lib/utils";
import { enqueue, getQueue, onSynced } from "../lib/outbox";
import { fetchWithSnapshot, applyPendingWrites, KEYS } from "../lib/snapshot";
import { productMatchesQuery } from "../lib/search";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const API = `${API_BASE}/api`;

const SORT_OPTIONS = [
  { id: "name", label: "Name A–Z / नाम" },
  { id: "stock_asc", label: "Stock: low first / कम स्टक" },
  { id: "price_desc", label: "Price: high first / मूल्य" },
  { id: "size", label: "Size: small first / नाप" },
  { id: "updated", label: "Recently updated / भर्खरको" },
];

export default function Inventory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeader } = useAuth();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showLowStock, setShowLowStock] = useState(
    searchParams.get("filter") === "low-stock",
  );
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState({});
  const [sizeUnits, setSizeUnits] = useState({});
  // For the tag code on each card: the minimum margin behind the floor, and
  // the shop's cipher word.
  const [shop, setShop] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Refresh once a background drain reaches the server, so quantities stop
  // showing the pre-sync figures.
  useEffect(() => onSynced(fetchData), []);

  useEffect(() => {
    filterProducts();
  }, [products, search, selectedCategory, showLowStock, showOutOfStock, sortBy]);

  const fetchData = async () => {
    // Each list falls back to its own saved copy, so a dead connection shows
    // the shop's real inventory instead of an empty page reading "No products
    // yet" -- which told the shopkeeper their shop was empty.
    let anyStale = false;
    try {
      const products = await fetchWithSnapshot(KEYS.products, async () => {
        const res = await axios.get(`${API}/products`, getAuthHeader());
        return res.data;
      });
      anyStale = anyStale || products.stale;
      setProducts(applyPendingWrites(products.data, getQueue()));
    } catch (err) {
      console.error("Failed to fetch products:", err);
      toast.error("Failed to load inventory / लोड हुन सकेन");
    }

    try {
      const categories = await fetchWithSnapshot(KEYS.categories, async () => {
        const res = await axios.get(`${API}/categories`, getAuthHeader());
        return res.data;
      });
      anyStale = anyStale || categories.stale;
      setCategories(categories.data);
    } catch (err) {
      // Categories only drive the filter chips; losing them is not fatal.
      console.error("Failed to fetch categories:", err);
    }

    // Needed to render a size the way the shop writes it ("नं. १४" but "२ लिटर")
    // and to know which items are measured rather than counted. Not fatal
    // either: without them a card just shows the bare number.
    const byId = (rows) => Object.fromEntries((rows || []).map((r) => [r.id, r]));
    try {
      const [u, su, sh] = await Promise.all([
        fetchWithSnapshot(KEYS.units, async () =>
          (await axios.get(`${API}/units`, getAuthHeader())).data),
        fetchWithSnapshot(KEYS.sizeUnits, async () =>
          (await axios.get(`${API}/size-units`, getAuthHeader())).data),
        fetchWithSnapshot(KEYS.shop, async () =>
          (await axios.get(`${API}/shop`, getAuthHeader())).data),
      ]);
      setUnits(byId(u.data));
      setSizeUnits(byId(su.data));
      setShop(sh.data);
    } catch (err) {
      console.error("Failed to fetch units:", err);
    }

    setOffline(anyStale);
    setLoading(false);
  };

  const filterProducts = () => {
    let filtered = [...products];

    if (search) {
      filtered = filtered.filter((p) => productMatchesQuery(p, search));
    }

    if (selectedCategory !== "all") {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (showOutOfStock) {
      filtered = filtered.filter((p) => p.quantity <= 0);
    } else if (showLowStock) {
      filtered = filtered.filter((p) => p.quantity <= p.low_stock_threshold);
    }

    const sorters = {
      name: (a, b) => a.name_en.localeCompare(b.name_en),
      stock_asc: (a, b) => a.quantity - b.quantity,
      price_desc: (a, b) => (b.mrp || 0) - (a.mrp || 0),
      // Numeric, not lexical: as strings "10" sorts before "9", which would
      // read 1.5, 10, 2, 3 down a list of cooker sizes.
      size: (a, b) =>
        a.name_en.localeCompare(b.name_en) ||
        (a.size_value ?? Infinity) - (b.size_value ?? Infinity) ||
        (a.gauge ?? 0) - (b.gauge ?? 0),
      updated: (a, b) =>
        new Date(b.updated_at || 0) - new Date(a.updated_at || 0),
    };
    filtered.sort(sorters[sortBy] || sorters.name);

    setFilteredProducts(filtered);
  };

  // Optimistic quick stock update, sent as a relative change so it stays
  // correct if it has to wait in the outbox: replaying "set to 7" would undo
  // whatever happened meanwhile, while replaying "+1" composes. The adjustment
  // carries its own id so a replay is recognised and not applied twice.
  const handleStockChange = async (product, newQty) => {
    const prevQty = product.quantity;
    const prevUpdatedAt = product.updated_at;
    const delta = newQty - prevQty;
    if (delta === 0) return;

    const payload = { id: crypto.randomUUID(), delta };
    const path = `/products/${product.id}/stock/adjust`;

    // Mirror the updated_at the server will write, so the "recently updated"
    // sort reflects this change without waiting for a refetch.
    setProducts((prev) =>
      prev.map((p) =>
        p.id === product.id
          ? { ...p, quantity: newQty, updated_at: new Date().toISOString() }
          : p,
      ),
    );
    try {
      await axios.post(`${API}${path}`, payload, getAuthHeader());
    } catch (err) {
      const status = err.response?.status;
      // A 4xx is a real refusal and will not become valid later; anything else
      // is the connection, so the adjustment goes in the queue.
      if (!status || status >= 500) {
        try {
          enqueue({
            method: "post",
            path,
            body: payload,
            label: `${product.name_en} ${delta > 0 ? "+" : ""}${delta}`,
          });
          return; // optimistic value stands; the queue owns it now
        } catch (storageErr) {
          console.error("Could not queue stock change:", storageErr);
        }
      }

      console.error("Stock update failed:", err);
      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id
            ? { ...p, quantity: prevQty, updated_at: prevUpdatedAt }
            : p,
        ),
      );
      toast.error("Could not update stock / स्टक अपडेट भएन");
    }
  };

  const handleProductClick = (product) => {
    navigate(`/inventory/${product.id}`);
  };

  /**
   * Fold an item's sizes into one row.
   *
   * A shop holding three cooker sizes, three thali gauges and six karai numbers
   * has eighteen rows that all read much the same. Grouping them turns that back
   * into three lines you can scan. Products with no group, or the only one in
   * theirs after filtering, stay as plain cards -- a group of one is just a card
   * with an extra tap.
   *
   * Order follows the sort, so a group appears where its first member would have.
   */
  const groups = useMemo(() => {
    const counts = {};
    for (const p of filteredProducts) {
      if (p.variant_group) counts[p.variant_group] = (counts[p.variant_group] || 0) + 1;
    }

    const out = [];
    const seen = {};
    for (const p of filteredProducts) {
      const key = p.variant_group;
      if (!key || counts[key] < 2) {
        out.push({ kind: "single", product: p });
        continue;
      }
      if (seen[key] === undefined) {
        seen[key] = out.length;
        out.push({ kind: "group", key, name_en: p.name_en, name_np: p.name_np, items: [p] });
      } else {
        out[seen[key]].items.push(p);
      }
    }
    return out;
  }, [filteredProducts]);

  /**
   * Label a member of a group by whatever actually tells it apart.
   *
   * Usually that is the size, but the three thali are all 11 inch and differ
   * only in gauge -- labelling those by size gives three identical chips, which
   * says nothing. So each part is shown only if it varies within the group.
   */
  const distinguisher = useCallback(
    (product, siblings) => {
      const varies = (field) =>
        new Set(siblings.map((p) => p[field] ?? null)).size > 1;
      const parts = [];
      if (varies("size_value")) parts.push(formatSize(product, sizeUnits));
      if (varies("gauge") && product.gauge != null) {
        parts.push(`${formatQty(product.gauge)} गेज`);
      }
      if (varies("brand") && product.brand) parts.push(product.brand);
      // Nothing varies: rows are true duplicates, so fall back to the size
      // rather than an empty chip.
      if (!parts.length) parts.push(formatSize(product, sizeUnits));
      return parts.filter(Boolean).join(" · ");
    },
    [sizeUnits],
  );

  // Collapsed by default so a long inventory is scannable, but a search means
  // the shopkeeper is after something specific and wants to see it.
  const searching = Boolean(search) || showLowStock || showOutOfStock;
  const isOpen = (key) => (key in collapsed ? collapsed[key] : searching);
  const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !isOpen(key) }));

  const card = (product) => (
    <ProductCard
      key={product.id}
      product={product}
      onClick={handleProductClick}
      onStockChange={handleStockChange}
      sizeUnits={sizeUnits}
      units={units}
      shop={shop}
    />
  );

  const categoryList = [
    { id: "all", name_en: "All Items", name_np: "सबै" },
    ...categories,
  ];

  // Summary metrics across the full (unfiltered) active inventory.
  // Stock value is at COST, matching inventory_value in /api/dashboard/stats —
  // the two must agree. At the tag price this figure read roughly double the
  // money actually tied up in the shelves.
  const totalUnits = products.reduce((sum, p) => sum + (p.quantity || 0), 0);
  const stockValue = products.reduce(
    (sum, p) => sum + (p.cost_price || 0) * (p.quantity || 0),
    0,
  );
  const lowStockCount = products.filter(
    (p) => p.quantity <= p.low_stock_threshold,
  ).length;

  return (
    <Layout>
      <div className="space-y-4 animate-fadeIn" data-testid="inventory-page">
        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-2" data-testid="inventory-summary">
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className="text-lg font-bold text-[#2D2D2D]">{products.length}</p>
            <p className="text-xs text-gray-500 font-nepali">वस्तु ({formatQty(totalUnits)} थान)</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className="text-lg font-bold text-[#8B0000]">{formatNPR(stockValue)}</p>
            <p className="text-xs text-gray-500 font-nepali">स्टक मूल्य</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
            <p className={`text-lg font-bold ${lowStockCount > 0 ? "text-amber-600" : "text-[#2D2D2D]"}`}>
              {lowStockCount}
            </p>
            <p className="text-xs text-gray-500 font-nepali">कम स्टक</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search products... / खोज्नुहोस्..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
            data-testid="search-input"
          />
          {search && (
            <button onClick={() => setSearch("")}>
              <X className="w-5 h-5 text-gray-400" />
            </button>
          )}
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          {categoryList.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`category-pill ${selectedCategory === cat.id ? "active" : ""}`}
              data-testid={`category-${cat.id}`}
            >
              <span className="font-nepali">{cat.name_np}</span>
            </button>
          ))}
        </div>

        {/* Filters + Sort */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowLowStock(!showLowStock);
                setShowOutOfStock(false);
              }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showLowStock
                  ? "bg-amber-100 text-amber-700 border border-amber-200"
                  : "bg-gray-100 text-gray-600"
              }`}
              data-testid="low-stock-filter"
            >
              <AlertTriangle className="w-4 h-4" />
              Low
            </button>
            <button
              onClick={() => {
                setShowOutOfStock(!showOutOfStock);
                setShowLowStock(false);
              }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showOutOfStock
                  ? "bg-red-100 text-red-700 border border-red-200"
                  : "bg-gray-100 text-gray-600"
              }`}
              data-testid="out-of-stock-filter"
            >
              <PackageX className="w-4 h-4" />
              Out
            </button>
          </div>

          <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg px-2 py-1.5">
            <ArrowUpDown className="w-4 h-4 text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent text-sm text-gray-700 focus:outline-none"
              data-testid="sort-select"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-right">
          <span className="text-sm text-gray-500">
            {filteredProducts.length} items
            {groups.length !== filteredProducts.length &&
              ` in ${groups.length} rows`}
          </span>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loader"></div>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div
            // pb-24 keeps the last card clear of the fixed FAB
            className="grid grid-cols-1 gap-3 pb-24"
          >
            {groups.map((row) => {
              if (row.kind === "single") return card(row.product);

              const open = isOpen(row.key);
              const stock = row.items.reduce((n, p) => n + (p.quantity || 0), 0);
              const prices = row.items.map((p) => p.mrp || 0).filter(Boolean);
              const low = Math.min(...prices);
              const high = Math.max(...prices);

              return (
                <div key={row.key} data-testid={`variant-group-${row.key}`}>
                  <button
                    onClick={() => toggle(row.key)}
                    className="w-full bg-white rounded-xl p-4 flex items-center gap-3 text-left
                               border border-gray-100 active:scale-[0.99] transition"
                    aria-expanded={open}
                  >
                    {open ? (
                      <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#2D2D2D] truncate">
                        {row.name_en}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {row.items.length} sizes
                        </span>
                      </h3>
                      <p className="text-sm text-gray-500 font-nepali truncate">
                        {row.name_np} · {formatQty(stock)} थान ·{" "}
                        {low === high
                          ? formatNPR(low)
                          : `${formatNPR(low)}–${formatNPR(high)}`}
                      </p>
                    </div>
                    {/* The sizes themselves, so a collapsed row still says what
                        it contains rather than only how many. */}
                    {!open && (
                      <div className="hidden sm:flex gap-1 shrink-0">
                        {row.items.slice(0, 4).map((p) => (
                          <span
                            key={p.id}
                            className="px-1.5 py-0.5 rounded bg-gray-100 text-xs text-gray-600 font-nepali"
                          >
                            {distinguisher(p, row.items) || "—"}
                          </span>
                        ))}
                        {row.items.length > 4 && (
                          <span className="text-xs text-gray-400 self-center">…</span>
                        )}
                      </div>
                    )}
                  </button>

                  {open && (
                    <div className="mt-2 ml-4 pl-3 border-l-2 border-gray-200 space-y-2">
                      {row.items.map(card)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Package className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-[#2D2D2D] mb-2">
              {/* Never claim the shop is empty when we simply could not load
                  it -- that reads as "your stock is gone". */}
              {offline && products.length === 0
                ? "Cannot load inventory"
                : search || selectedCategory !== "all" || showLowStock || showOutOfStock
                  ? "No matching products"
                  : "No products yet"}
            </h3>
            <p className="text-gray-500 mb-4 font-nepali">
              {offline && products.length === 0
                ? "इन्टरनेट जोडिएपछि देखिनेछ"
                : search || selectedCategory !== "all" || showLowStock || showOutOfStock
                  ? "Try different search or filter"
                  : "सामान थप्न सुरु गर्नुहोस्"}
            </p>
            {!offline && !search && selectedCategory === "all" && !showLowStock && !showOutOfStock && (
              <button
                onClick={() => navigate("/inventory/add")}
                className="px-6 py-3 bg-[#8B0000] text-white rounded-xl font-medium"
              >
                Add First Product
              </button>
            )}
          </div>
        )}

        {/* FAB - Add Product */}
        <button
          onClick={() => navigate("/inventory/add")}
          className="fab"
          data-testid="add-product-fab"
        >
          <Plus className="w-7 h-7" />
        </button>
      </div>
    </Layout>
  );
}
